/**
 * AgentTimeline — Phase 1 of ADR-095 chat-ui v2 refresh.
 *
 * Consolidates the previously-separate AgentStateStepper + ToolGroup +
 * ReasoningPanel surfaces into ONE vertical timeline matching open-webui's
 * StatusHistory pattern (per the 2026-05-31 chat-ui audit, item #1).
 *
 * Data model — derives a typed `TimelineRow[]` from the assistant
 * `UiMessage`:
 *   - `"stage"`     — synthetic planning / searching / synthesizing rows
 *                     derived from message state. One row per stage.
 *   - `"tool"`      — one row per ToolTrace (preserves the expandable args
 *                     + result-preview UX from the absorbed ToolGroup).
 *   - `"reasoning"` — one row per call_site (planner / decomposer /
 *                     synthesizer / synthesizer.grouped) from
 *                     ReasoningState keyed by call_site in chatStore.
 *
 * Rendering:
 *   - Vertical layout with 6px status dots + 0.5px connector line between
 *     consecutive rows.
 *   - Active row uses a horizontal shimmer gradient on its label text
 *     (CSS @keyframes bk-shimmer in globals.css, .bk-anim-shimmer class).
 *   - Live reasoning row preserves the 250ms-tick interval from the
 *     absorbed ReasoningPanel for elapsed-time display.
 */

import { useEffect, useState } from "react";

import { ToolTimelineRow } from "@/components/thread/ToolTimelineRow";
import { labelFor as toolLabelFor } from "@/lib/toolLabels";
import { type ReasoningState, type UiMessage, useChatStore, type VerifierState } from "@/runtime/chatStore";
import type { ToolTrace } from "@/types";

export type StageStatus = "pending" | "active" | "done";

export type TimelineRow =
  | { kind: "stage"; key: string; label: string; status: StageStatus; toolHint?: string | null }
  | { kind: "tool"; trace: ToolTrace }
  | { kind: "reasoning"; state: ReasoningState }
  | { kind: "verifier"; state: VerifierState };

export interface AgentTimelineProps {
  message: UiMessage;
  isStreaming: boolean;
}

const SITE_LABELS: Record<string, string> = {
  synthesizer: "Reasoning on retrieved evidence",
  "synthesizer.grouped": "Reasoning across sub-questions",
  planner: "Planning retrieval",
  decomposer: "Decomposing multi-hop query",
};

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function deriveStages(message: UiMessage, isStreaming: boolean, isReplaying: boolean): TimelineRow[] {
  const hasTraces = message.toolTraces.length > 0;
  const hasContent = message.content.length > 0;
  // Treat HISTORY replay as "complete" for stage-dot derivation so the
  // synthesizing/searching/planning dots render as "done" (green) and the
  // synthesizing label does NOT show the bk-anim-shimmer active animation
  // during the ~30s SSE replay window. Composer's Stop button still uses
  // isStreaming directly so Send stays disabled while events arrive.
  const isComplete = !isStreaming || isReplaying;

  // Stage transitions (preserved from the absorbed AgentStateStepper logic,
  // extended with "done" when streaming has completed):
  //   - Planning: done once any work signal (traces / content / complete);
  //               active while streaming and no signal yet.
  //   - Searching: done when content streams (synthesis started) OR when
  //                streaming completed; active when traces fired but no
  //                content yet; pending otherwise.
  //   - Synthesizing: done when streaming completed AND content present;
  //                   active while content is being streamed; pending else.
  let planning: StageStatus = "active";
  if (hasTraces || hasContent || isComplete) planning = "done";

  let searching: StageStatus = "pending";
  if (hasContent || isComplete) {
    searching = "done";
  } else if (hasTraces) {
    searching = "active";
  }

  let synthesizing: StageStatus = "pending";
  if (isComplete && hasContent) {
    synthesizing = "done";
  } else if (hasContent) {
    synthesizing = "active";
  }

  // Active tool hint surfaces below "Searching docs" stage when a tool is
  // currently in-flight (preserved from AgentStateStepper.activeToolLabel).
  // Gate on isStreaming AND not-replaying so (a) a dropped tool_call_end
  // event (SSE relay close, ACA revision swap, network drop) doesn't leave
  // the hint hanging as a stuck "→ toolname" indicator after the stream
  // ends, AND (b) the hint doesn't flash during the HISTORY replay window.
  const activeTrace = isStreaming && !isReplaying ? message.toolTraces.find((t) => t.duration_ms === 0) : undefined;
  const toolHint = activeTrace ? toolLabelFor(activeTrace.tool) : null;

  return [
    { kind: "stage", key: "planning", label: "Planning", status: planning },
    { kind: "stage", key: "searching", label: "Searching docs", status: searching, toolHint },
    { kind: "stage", key: "synthesizing", label: "Synthesizing", status: synthesizing },
  ];
}

function deriveRows(message: UiMessage, isStreaming: boolean, isReplaying: boolean): TimelineRow[] {
  const stages = deriveStages(message, isStreaming, isReplaying);
  const planning = stages[0];
  const searching = stages[1];
  const synthesizing = stages[2];
  const toolRows: TimelineRow[] = message.toolTraces.map((t) => ({ kind: "tool", trace: t }));
  const reasoningRows: TimelineRow[] = message.reasoning
    ? Object.values(message.reasoning).map((state) => ({ kind: "reasoning", state }))
    : [];
  const verifierRows: TimelineRow[] = message.verifier
    ? Object.values(message.verifier)
        .sort((a, b) => a.iteration - b.iteration)
        .map((state) => ({ kind: "verifier", state }))
    : [];

  // Temporal order: planning → searching → tool rows (sub-steps of searching)
  // → synthesizing → reasoning rows (sub-steps of synthesizing) → verifier
  // rows (post-synth gate, ADR-092 Phase 1c). Verifier renders LAST so it
  // reads as "the answer was checked and accepted/rejected" at the bottom
  // of the rail.
  return [planning, searching, ...toolRows, synthesizing, ...reasoningRows, ...verifierRows];
}

function dotStatusFor(row: TimelineRow, isStreaming: boolean, isReplaying: boolean): StageStatus {
  if (row.kind === "stage") return row.status;
  if (row.kind === "tool") {
    // Tool-row "active" requires BOTH duration_ms === 0 AND isStreaming
    // AND NOT isReplaying. Without the isStreaming gate, a dropped
    // tool_call_end event (SSE relay close before delivery, ACA revision
    // swap) leaves duration_ms=0 permanently and the animate-ping blue dot
    // stays visible after the stream ends. Without the isReplaying gate,
    // the dot pulses during the HISTORY replay window even though the job
    // already completed. All clearing paths (case 'complete', case 'error',
    // endStream, watchdog) flip isStreaming → false; pumpJobReplay's finally
    // path flips isReplaying → false. The dot is "done" in either terminal.
    return isStreaming && !isReplaying && row.trace.duration_ms === 0 ? "active" : "done";
  }
  if (row.kind === "verifier") {
    // Mirror the reasoning isLive gating exactly — durationMs undefined is
    // the structural signal that the Complete event has not yet arrived.
    // Accepted vs rejected is encoded in text color (rose for rejected) at
    // VerifierRowContent render time, NOT in the dot status: both terminals
    // render as "done" (emerald) because the verifier itself completed.
    const isLive = isStreaming && !isReplaying && row.state.durationMs === undefined;
    return isLive ? "active" : "done";
  }
  const isLive = isStreaming && !isReplaying && row.state.durationMs === undefined;
  return isLive ? "active" : "done";
}

function TimelineDot({ status }: { status: StageStatus }) {
  if (status === "done") {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--status-success)]"
        data-status="done"
      />
    );
  }
  if (status === "active") {
    return (
      <span aria-hidden="true" className="relative inline-flex h-1.5 w-1.5 flex-shrink-0" data-status="active">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--accent-primary)] opacity-40" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)]" />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-1.5 w-1.5 flex-shrink-0 rounded-full border border-[var(--text-tertiary)] bg-transparent"
      data-status="pending"
    />
  );
}

function StageRowContent({ row }: { row: Extract<TimelineRow, { kind: "stage" }> }) {
  const isActive = row.status === "active";
  return (
    <div>
      <span
        className={`type-body-sm font-medium ${
          isActive
            ? "bk-anim-shimmer"
            : row.status === "done"
              ? "text-[var(--text-primary)]"
              : "text-[var(--text-tertiary)]"
        }`}
      >
        {row.label}
      </span>
      {row.toolHint && (
        <span className="ml-[var(--space-2)] type-caption text-[var(--text-tertiary)]">
          <span className="font-mono">→</span> {row.toolHint}
        </span>
      )}
    </div>
  );
}

function ReasoningRowContent({
  state,
  isStreaming,
  isReplaying,
}: {
  state: ReasoningState;
  isStreaming: boolean;
  isReplaying: boolean;
}) {
  const isLive = isStreaming && !isReplaying && state.durationMs === undefined;
  const [tickMs, setTickMs] = useState(() => Date.now() - state.startedAt);

  useEffect(() => {
    if (!isLive) return;
    const handle = window.setInterval(() => {
      setTickMs(Date.now() - state.startedAt);
    }, 250);
    return () => window.clearInterval(handle);
  }, [isLive, state.startedAt]);

  const siteLabel = SITE_LABELS[state.callSite] ?? state.callSite;

  if (isLive) {
    return (
      <div className="type-body-sm">
        <span className="bk-anim-shimmer font-medium">Reasoning</span>
        <span className="ml-[var(--space-1)] text-[var(--text-secondary)]">· {siteLabel}</span>
        <span className="ml-[var(--space-1)] tabular-nums text-[var(--text-secondary)]">{formatDuration(tickMs)}</span>
      </div>
    );
  }

  if (state.durationMs === undefined) {
    return null;
  }

  return (
    <details className="type-body-sm">
      <summary className="cursor-pointer select-none list-none">
        <span className="font-medium text-[var(--text-primary)]">Reasoned</span>{" "}
        <span className="tabular-nums text-[var(--text-secondary)]">for {formatDuration(state.durationMs)}</span>
      </summary>
      <div className="mt-[var(--space-1)] ml-0.5 space-y-0.5 border-l border-[var(--border-default)] pl-[var(--space-2)] text-[var(--text-secondary)]">
        <div>
          <span className="opacity-70">Stage:</span> {siteLabel}
        </div>
        <div>
          <span className="opacity-70">Effort:</span> {state.effort}
        </div>
        {typeof state.tokens === "number" && state.tokens > 0 && (
          <div>
            <span className="opacity-70">Tokens:</span> {formatTokens(state.tokens)}
          </div>
        )}
      </div>
    </details>
  );
}

function VerifierRowContent({
  state,
  isStreaming,
  isReplaying,
}: {
  state: VerifierState;
  isStreaming: boolean;
  isReplaying: boolean;
}) {
  const isLive = isStreaming && !isReplaying && state.durationMs === undefined;
  const [tickMs, setTickMs] = useState(() => Date.now() - state.startedAt);

  useEffect(() => {
    if (!isLive) return;
    const handle = window.setInterval(() => {
      setTickMs(Date.now() - state.startedAt);
    }, 250);
    return () => window.clearInterval(handle);
  }, [isLive, state.startedAt]);

  if (isLive) {
    return (
      <div className="type-body-sm">
        <span className="bk-anim-shimmer font-medium">Verifying</span>
        <span className="ml-[var(--space-1)] text-[var(--text-secondary)]">
          · attempt {state.iteration}/{state.maxIterations}
        </span>
        <span className="ml-[var(--space-1)] tabular-nums text-[var(--text-secondary)]">{formatDuration(tickMs)}</span>
      </div>
    );
  }

  if (state.durationMs === undefined) {
    return null;
  }

  const accepted = state.accepted === true;
  const score = state.supportedFraction !== undefined ? state.supportedFraction.toFixed(2) : "?.??";
  return (
    <details className="type-body-sm">
      <summary className="cursor-pointer select-none list-none">
        <span className={`font-medium ${accepted ? "text-[var(--bk-fg)]" : "text-[var(--status-error)]"}`}>
          {accepted ? "Verified" : "Verification rejected"}
        </span>{" "}
        <span className="tabular-nums text-[var(--text-secondary)]">· {score} supported</span>
      </summary>
      <div className="mt-[var(--space-1)] ml-0.5 space-y-0.5 border-l border-[var(--border-default)] pl-[var(--space-2)] text-[var(--text-secondary)]">
        <div>
          <span className="opacity-70">Iteration:</span> {state.iteration} of {state.maxIterations}
        </div>
        <div>
          <span className="opacity-70">Duration:</span> {formatDuration(state.durationMs)}
        </div>
        <div>
          <span className="opacity-70">Floor:</span> {state.supportedFractionFloor.toFixed(2)}
        </div>
      </div>
    </details>
  );
}

function rowKey(row: TimelineRow): string {
  if (row.kind === "stage") return `stage-${row.key}`;
  if (row.kind === "tool") return `tool-${row.trace.call_id}`;
  if (row.kind === "verifier") return `verifier-${row.state.iteration}`;
  return `reasoning-${row.state.callSite}`;
}

export function AgentTimeline({ message, isStreaming }: AgentTimelineProps) {
  const isReplaying = useChatStore((s) => s.isReplaying);
  const toolCount = message.toolTraces.length;
  const reasoningStates = message.reasoning ? Object.values(message.reasoning) : [];
  const hasContent = toolCount > 0 || reasoningStates.length > 0;
  // showCollapseToggle = TRUE when streaming has effectively ended (real
  // termination OR replay-of-completed-job) AND there's content to collapse.
  // During HISTORY replay, the user wants to see the "Researched in N s"
  // summary immediately, not the expanded 3-stage rail with shimmer.
  const showCollapseToggle = (!isStreaming || isReplaying) && hasContent;
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setExpanded(!showCollapseToggle);
  }, [showCollapseToggle]);

  // Hide the rail entirely for fully-empty messages once streaming has ended.
  // loadJobFromHistory's startAssistantMessage() placeholder (chatStore.ts
  // line 380) can leave an empty assistant message visible in the thread
  // when subsequent SSE replay events land in a different message slot.
  // Without this guard, the default 3-stage rail renders with a pending
  // gray dot + "Synthesizing" label, which operator reads as "Synthesizing
  // still running" despite no actual in-flight work. Streaming messages
  // still render even when empty (active rail is the user-visible signal
  // that work is happening). Early-return MUST be AFTER hooks per React
  // Rules of Hooks — moving hooks before the guard preserves call order
  // across renders.
  if (
    !isStreaming &&
    !hasContent &&
    message.content.length === 0 &&
    (message.terminated_reason === undefined || message.terminated_reason === null)
  ) {
    return null;
  }

  const rows = deriveRows(message, isStreaming, isReplaying);
  if (rows.length === 0) return null;

  const totalMs =
    message.toolTraces.reduce((sum, t) => sum + (t.duration_ms || 0), 0) +
    reasoningStates.reduce((sum, r) => sum + (r.durationMs || 0), 0);

  return (
    <div
      data-testid="agent-timeline"
      role="status"
      aria-live="polite"
      className="mb-4 mt-2 border-l border-[var(--border-default)] pl-3"
    >
      {showCollapseToggle && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="-ml-3 flex items-center gap-1.5 py-1 pl-3 type-body-sm text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-[var(--text-primary)]"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className={`transition-transform ${expanded ? "rotate-90" : ""}`}
            aria-hidden="true"
          >
            <path d="M3 2l3 3-3 3" />
          </svg>
          <span>Researched in {formatDuration(totalMs)}</span>
        </button>
      )}
      <ol className={showCollapseToggle && !expanded ? "sr-only" : ""}>
        {rows.map((row, idx) => {
          const isLast = idx === rows.length - 1;
          const status = dotStatusFor(row, isStreaming, isReplaying);
          return (
            <li key={rowKey(row)} className="flex gap-3" data-kind={row.kind}>
              <div className="flex w-1.5 flex-col items-center pt-1.5">
                <TimelineDot status={status} />
                {!isLast && (
                  <span
                    aria-hidden="true"
                    className="w-px flex-1 bg-[var(--border-default)]"
                    style={{ minHeight: "0.75rem" }}
                  />
                )}
              </div>
              <div className={`min-w-0 flex-1 ${isLast ? "" : "pb-2"}`}>
                {row.kind === "stage" && <StageRowContent row={row} />}
                {row.kind === "tool" && <ToolTimelineRow trace={row.trace} />}
                {row.kind === "reasoning" && (
                  <ReasoningRowContent state={row.state} isStreaming={isStreaming} isReplaying={isReplaying} />
                )}
                {row.kind === "verifier" && (
                  <VerifierRowContent state={row.state} isStreaming={isStreaming} isReplaying={isReplaying} />
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {message.verifierVerdict && (
        <div
          data-testid="verifier-verdict-tail"
          className={`mt-2 inline-flex items-center gap-1 type-caption ${
            message.verifierVerdict.accepted ? "text-[var(--status-success)]" : "text-[var(--status-error)]"
          }`}
          title={`Verifier floor: ${(message.verifierVerdict.floor * 100).toFixed(0)}%`}
        >
          <span aria-hidden="true">{message.verifierVerdict.accepted ? "✓" : "⚠"}</span>
          <span>
            {(message.verifierVerdict.supportedFraction * 100).toFixed(0)}% verified (iter{" "}
            {message.verifierVerdict.iteration})
          </span>
        </div>
      )}
    </div>
  );
}
